import { Label } from '@oberoninternal/travelbase-ds/components/primitive/Label';
import { Box } from '@rebass/grid';
import React, { FC, memo, useCallback } from 'react';
import { areEqual, ListChildComponentProps } from 'react-window';
import styled from 'styled-components/macro';
import useBigList, { CreateRow, CreateSkeletonRows } from '../../../hooks/useBigList';
import { formatDuration } from '../../../utils/formatDuration';
import BigListWrapper from '../../atoms/BigListWrapper';

import BigTableHeader from '../../atoms/BigTableHeader';
import UnitDetails from '../../atoms/UnitDetails';
import BigList from '../../molecules/BigList';
import FilterUnitMenu from '../../molecules/FilterUnitMenu';
import { STEP } from '../Bookings';
import { getGuestTitle } from '../bookings/Details';

import {
    useDeleteVisitorRegistrationMutation,
    RentalUnit,
    IdentificationTypeEnum,
    AllVisitorRegistrationsDocument,
} from '../../../generated/graphql';
import Cross from '@oberoninternal/travelbase-ds/components/figure/Cross';
import { Done } from '@oberoninternal/travelbase-ds/components/figure/Done';
import TextButton from '@oberoninternal/travelbase-ds/components/action/TextButton';
import { Maybe } from '@oberoninternal/travelbase-ds/entities/Maybe';
import { useParams } from 'react-router-dom';
import { useQueryParams, StringParam, withDefault, ArrayParam } from 'use-query-params';
import isValidDate from '../../../utils/isValidDate';
import { FormattedMessage, useIntl } from 'react-intl';
import Plus from '@oberoninternal/travelbase-ds/components/figure/Plus';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { useThemeContext } from '../../../hooks/useThemeContext';
import VisitorRegistrationSidebar from './VisitorRegistrationSidebar';

export const identificationTypeTranslations: Record<IdentificationTypeEnum, string> = {
    CARD: 'Identiteitskaart',
    DRIVING_LICENSE: 'Rijbewijs',
    PASSPORT: 'Paspoort',
};

export type VisitorNode = {
    node?: Maybe<{
        rentalUnit: Pick<RentalUnit, 'id' | 'name' | 'slug' | 'type' | 'code' | 'thumbnailUrl' | 'hideForPartner'>;
        arrivalDate: string;
        departureDate: string;
        firstName: string;
        lastName: string;
        city: string;
        countryCode: string;
        id: string;
        totalPersons: number;
        identificationType: IdentificationTypeEnum;
        isManual: boolean;
    }>;
};

export type VisitorRegistration = {
    edges?: Maybe<VisitorNode>[] | null;
};

interface RegisterHistoryListProps {
    entries?: Maybe<VisitorRegistration>;
    isLoading: boolean;
    isSearching?: boolean;
    noResultsText: string;

    onLoadMore?: (startIndex: number, stopIndex: number) => Promise<void>;
    hasNextPage?: boolean;
    isNextPageLoading?: boolean;
}

const widths = [
    [3 / 16, null, 2 / 8],
    [3 / 16, null, 3 / 16],
    [3 / 16, null, 3 / 16],
    [2 / 16, null, 1 / 8],
    [2 / 16, null, 3 / 16],
    [2 / 16, null, 2 / 16],
    [2 / 16, null, 1 / 8],
];

const VisitorRegistrationHistoryList: FC<RegisterHistoryListProps> = memo(
    ({ entries, isLoading, noResultsText, isSearching }) => {
        const { createSkeletonRows, createRow } = useBigList(widths);
        const { formatMessage } = useIntl();
        const [activeRegistrationId, setActiveRegistrationId] = React.useState<string | undefined>(undefined);

        const sesame = useSesame(false, {
            closeOnClickOutside: true,
            closeOnEscape: true,
        });

        const onOpenSidebarWithoutId = useCallback(() => {
            setActiveRegistrationId(undefined);
            sesame.onOpen();
        }, [sesame]);

        const onOpenVisitorRegistrationSidebar = useCallback(
            (id: string) => {
                setActiveRegistrationId(id);
                sesame.onOpen();
            },
            [sesame, setActiveRegistrationId]
        );

        const Item: FC<ListChildComponentProps> = props => (
            <RegistrationItem onOpen={onOpenVisitorRegistrationSidebar} {...props} />
        );
        const visitorRegistrations = entries?.edges;
        const activeRegistration = visitorRegistrations?.find(edge => edge?.node?.id === activeRegistrationId);

        return (
            <BigListWrapper>
                <AddVisitorRegisterButton onClick={onOpenSidebarWithoutId}>
                    <Plus />
                    <span>
                        <FormattedMessage defaultMessage="Voeg handmatig toe" />
                    </span>
                </AddVisitorRegisterButton>
                <BigTableHeader>
                    {[
                        <FilterUnitMenu key="unit-filter" />,
                        formatMessage({ defaultMessage: 'Periode' }),
                        formatMessage({ defaultMessage: 'Gast ' }),
                        formatMessage({ defaultMessage: 'Woonplaats' }),
                        formatMessage({ defaultMessage: 'Identiteitsbewijs' }),
                        formatMessage({ defaultMessage: 'Handmatig' }),
                        '',
                    ].map((header, i) => (
                        <Box key={i} width={widths[i]}>
                            {typeof header === 'string' ? <Label>{header}</Label> : header}
                        </Box>
                    ))}
                </BigTableHeader>

                {!isSearching ? (
                    !isLoading && visitorRegistrations?.length === 0 ? (
                        <Box p={4}>{noResultsText}</Box>
                    ) : (
                        <BigList
                            overscanCount={STEP * 2}
                            itemCount={visitorRegistrations?.length ?? 0}
                            itemSize={78}
                            itemData={{
                                edges: visitorRegistrations,
                                createRow,
                                createSkeletonRows,
                                activeRegistrationId,
                            }}
                        >
                            {Item}
                        </BigList>
                    )
                ) : (
                    createSkeletonRows(STEP)
                )}
                <VisitorRegistrationSidebar
                    activeRegistration={activeRegistration}
                    sesame={sesame}
                    setActiveRegistrationId={setActiveRegistrationId}
                />
            </BigListWrapper>
        );
    }
);

export default VisitorRegistrationHistoryList;

interface RegistrationItemProps extends ListChildComponentProps {
    onOpen: (id: string) => void;
    data: {
        edges: VisitorRegistration['edges'];
        createRow: CreateRow;
        createSkeletonRows: CreateSkeletonRows;
        activeRegistrationId?: string;
    };
}

const RegistrationItem: FC<RegistrationItemProps> = memo(
    ({ onOpen, index, style, data: { createRow, createSkeletonRows, edges, activeRegistrationId } }) => {
        const handleDelete = async (event: React.MouseEvent) => {
            // Prevent onOpen function being called after deleting
            event.stopPropagation();
            const confirmation = window.confirm(
                formatMessage({
                    defaultMessage: `Weet je zeker dat je dit nachtregister wilt verwijderen?`,
                })
            );

            if (!confirmation) return;

            await deleteMutate({
                variables: { id },
            });
        };

        const registration = edges?.[index]?.node;
        const { partnerId } = useParams<{ partnerId: string }>();
        const [paramQuery] = useQueryParams({
            startDate: StringParam,
            endDate: StringParam,
            rentalUnitIds: withDefault(ArrayParam, []),
        });
        const { formatMessage } = useIntl();
        const theme = useThemeContext();
        const validStartDate = paramQuery.startDate && isValidDate(paramQuery.startDate) ? paramQuery.startDate : null;
        const validEndDate = paramQuery.endDate && isValidDate(paramQuery.endDate) ? paramQuery.endDate : null;
        const [deleteMutate] = useDeleteVisitorRegistrationMutation({
            refetchQueries: [
                {
                    query: AllVisitorRegistrationsDocument,
                    variables: {
                        partnerId,
                        startDate: validStartDate,
                        endDate: validEndDate,
                        rentalUnitIds: paramQuery.rentalUnitIds,
                        first: STEP * 4,
                    },
                },
            ],
        });

        if (!registration) {
            return createSkeletonRows(1, style);
        }
        const { id, rentalUnit, arrivalDate, departureDate, identificationType, firstName, lastName, city, isManual } =
            registration;

        const startDate = new Date(arrivalDate);
        const endDate = new Date(departureDate);
        const periodText = formatDuration(startDate, endDate);
        const isActive = activeRegistrationId === id;
        const rowStyle = isActive ? { ...style, background: theme.colors.primary[0], fontWeight: 600 } : style;
        return createRow(
            undefined,
            rowStyle,
            [
                <StyledUnitDetails key={id} unit={{ ...rentalUnit }} />,
                { text: periodText },
                {
                    text: getGuestTitle(
                        {
                            firstName,
                            lastName,
                        },
                        false
                    ),
                },
                {
                    text: city ?? '-',
                },
                {
                    text:
                        identificationTypeTranslations[
                            identificationType as keyof typeof identificationTypeTranslations
                        ] ?? '-',
                },
                ...(isManual
                    ? [
                          <Done key={`${id}-manual`} />,
                          <TextButton key={id} onClick={handleDelete} variant="danger" size="tiny">
                              <Cross />
                              <span>
                                  <FormattedMessage defaultMessage="Verwijderen" />
                              </span>
                          </TextButton>,
                      ]
                    : [<Cross key={`${id}-manual`} />, <span key={id} />]),
            ],
            () => onOpen(id)
        );
    },
    areEqual
);

const StyledUnitDetails = styled(UnitDetails)`
    min-width: 100%;

    @media (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        > :first-child {
            display: none;
        }
    }
`;

const AddVisitorRegisterButton = styled(Button).attrs(() => ({ variant: 'outline' }))`
    height: 7.2rem;
    width: 100%;
    color: ${({ theme }) => theme.colors.primary[40]};
    margin-bottom: 2rem;
    margin-top: 2rem;
`;
